import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import {
  getAdminStats,
  getAdminMsps,
  createMsp,
} from "../lib/api";
import {
  Users,
  Building2,
  DollarSign,
  UserPlus,
  Loader2,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

export const AdminPage = () => {
  const [stats, setStats] = useState(null);
  const [msps, setMsps] = useState({ items: [], total_items: 0 });
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingMsps, setLoadingMsps] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", email: "" });

  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      const data = await getAdminStats();
      setStats(data);
    } catch (err) {
      console.error("Admin stats:", err);
      toast.error("Failed to load admin stats");
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchMsps = async () => {
    setLoadingMsps(true);
    try {
      const data = await getAdminMsps(1, 50);
      setMsps({ items: data.items || [], total_items: data.total_items ?? 0 });
    } catch (err) {
      console.error("Admin MSPs:", err);
      toast.error("Failed to load customers");
    } finally {
      setLoadingMsps(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchMsps();
  }, []);

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (!createForm.name.trim() || !createForm.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setCreateLoading(true);
    try {
      await createMsp({ name: createForm.name.trim(), email: createForm.email.trim() });
      toast.success("Account created");
      setCreateForm({ name: "", email: "" });
      fetchStats();
      fetchMsps();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create account");
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">
          Admin
        </h1>
        <p className="text-muted-foreground mt-1">
          Platform overview, create accounts, and manage customers
        </p>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="create">Create account</TabsTrigger>
          <TabsTrigger value="customers">Customers</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {loadingStats ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Total signups (users)
                  </CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="font-heading text-3xl font-bold">
                    {stats?.total_users ?? "—"}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Signups (last 30 days)
                  </CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="font-heading text-3xl font-bold">
                    {stats?.signups_last_30_days ?? "—"}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Customers (MSPs)
                  </CardTitle>
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="font-heading text-3xl font-bold">
                    {stats?.total_msps ?? "—"}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Revenue (est. monthly)
                  </CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="font-heading text-3xl font-bold">
                    ${stats?.revenue_monthly_placeholder_usd ?? 0}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {stats?.paid_customers ?? 0} paid customers
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="create" className="space-y-6">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                Create account (MSP)
              </CardTitle>
              <CardDescription>
                Create a new customer/organization. They can then register users under this account.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateAccount} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="msp-name">Organization name</Label>
                  <Input
                    id="msp-name"
                    value={createForm.name}
                    onChange={(e) =>
                      setCreateForm((p) => ({ ...p, name: e.target.value }))
                    }
                    placeholder="Acme Inc."
                    disabled={createLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="msp-email">Email</Label>
                  <Input
                    id="msp-email"
                    type="email"
                    value={createForm.email}
                    onChange={(e) =>
                      setCreateForm((p) => ({ ...p, email: e.target.value }))
                    }
                    placeholder="admin@acme.com"
                    disabled={createLoading}
                  />
                </div>
                <Button type="submit" disabled={createLoading}>
                  {createLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating…
                    </>
                  ) : (
                    "Create account"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="customers" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Customers (MSPs)</CardTitle>
              <CardDescription>
                All organizations. Total: {msps.total_items}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="outline"
                size="sm"
                className="mb-4"
                onClick={fetchMsps}
                disabled={loadingMsps}
              >
                {loadingMsps ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Refresh list"
                )}
              </Button>
              {loadingMsps && msps.items.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : msps.items.length === 0 ? (
                <p className="text-muted-foreground py-6">No customers yet.</p>
              ) : (
                <div className="rounded-md border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="p-3 text-left font-medium">Name</th>
                        <th className="p-3 text-left font-medium">Email</th>
                        <th className="p-3 text-left font-medium">Tier</th>
                        <th className="p-3 text-left font-medium">Payment</th>
                        <th className="p-3 text-left font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {msps.items.map((msp) => (
                        <tr key={msp.id} className="border-b last:border-0">
                          <td className="p-3">{msp.name}</td>
                          <td className="p-3">{msp.email}</td>
                          <td className="p-3">{msp.subscription_tier}</td>
                          <td className="p-3">{msp.payment_status}</td>
                          <td className="p-3">
                            <Badge
                              variant={
                                msp.is_active ? "default" : "secondary"
                              }
                            >
                              {msp.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPage;
